import { useEffect, useRef, useState } from 'react'
import { api, type EngagementBudget as Budget } from '../../api/client'
import { useSession } from '../../api/session'

const money = (n: number) => n.toLocaleString(undefined, {style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4})
const message = (error: unknown) => error instanceof Error ? error.message : 'Could not update engagement budget'

export default function EngagementBudget({ engagementId }: {engagementId: string}) {
  const { user } = useSession()
  const [saved, setSaved] = useState<Budget | null>(null)
  const [editor, setEditor] = useState<{revision: number; cap: string} | null>(null)
  const [reason, setReason] = useState('')
  const [error, setError] = useState('')
  const [loadError, setLoadError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState(false)
  const pending = useRef(false)
  const generation = useRef(0)
  useEffect(() => {
    let live = true
    let timer: number | undefined
    const refresh = async () => {
      const requestGeneration = generation.current
      try {
        if (pending.current) return
        const value = await api.engagementBudget(engagementId)
        if (live && requestGeneration === generation.current) { setSaved(value); setLoadError('') }
      } catch (e) { if (live && requestGeneration === generation.current) setLoadError(message(e)) }
      finally { if (live) timer = window.setTimeout(refresh, 10000) }
    }
    void refresh()
    return () => { live = false; window.clearTimeout(timer) }
  }, [engagementId])
  async function run(action: () => Promise<void>) {
    if (pending.current) return
    pending.current = true; setBusy(true); setError(''); setNotice(''); generation.current += 1
    try { await action() } catch (e) { setError(message(e)) }
    finally { generation.current += 1; pending.current = false; setBusy(false) }
  }
  async function save(cap: number | null) {
    if (!editor || !reason.trim() || user?.role !== 'admin') return
    await run(async () => {
      if (cap !== null && (!Number.isFinite(cap) || cap <= 0)) throw new Error('Enter a positive cap in USD.')
      const value = await api.setEngagementBudget(engagementId, {cap_usd: cap, expected_revision: editor.revision, reason: reason.trim()})
      setSaved(value); setLoadError(''); setEditor(null); setReason(''); setNotice(cap === null ? 'Engagement cap removed. The lifetime cap still applies.' : 'Engagement cap saved.')
    })
  }
  return <section style={{marginBlock: '1.5rem'}}>
    <h3>Engagement budget</h3>
    <p className="muted">This optional cap limits preparation spending for this engagement. The lifetime cap also applies. Changing this cap does not enable outreach.</p>
    {loadError && <p className="notice crit" role="alert">{loadError} Retrying automatically; displayed costs may be out of date.</p>}
    {error && <p className="notice crit" role="alert">{error}</p>}
    {notice && <p role="status">{notice}</p>}
    {!saved ? <p className="muted">{loadError ? 'Waiting to reload engagement budget…' : 'Loading engagement budget…'}</p> : <>
      <p><strong>{money(saved.committed_usd)}</strong> recorded or reserved · {saved.cap_usd === null ? 'No engagement cap; lifetime cap applies' : `${money(saved.cap_usd)} engagement cap`}{saved.remaining_usd !== null && ` · ${money(saved.remaining_usd)} remaining`}</p>
      {saved.cap_usd !== null && saved.committed_usd >= saved.cap_usd && <p className="notice">The engagement cap is reached. Additional preparation spending is blocked.</p>}
      {saved.unpriced_events > 0 && <p className="notice">{saved.unpriced_events} events await pricing. Reconcile these charges before additional preparation spending; increasing or removing the cap does not clear this block. Retained reservations remain included.</p>}
      {saved.actor && <p className="muted">Last changed by {saved.actor}{saved.updated_at && ` · ${new Date(saved.updated_at).toLocaleString()}`}{saved.reason && ` · ${saved.reason}`}</p>}
      {user?.role === 'admin' && !editor && <button className="btn secondary small" disabled={busy} onClick={() => { setEditor({revision: saved.revision, cap: saved.cap_usd === null ? '' : String(saved.cap_usd)}); setReason(''); setNotice('') }}>Edit engagement cap</button>}
      {user?.role !== 'admin' && <p className="muted">Only an administrator can change this cap.</p>}
    </>}
    {user?.role === 'admin' && editor && <form className="reply" onSubmit={event => { event.preventDefault(); if (editor.cap.trim()) void save(Number(editor.cap)) }}>
      {saved && editor.revision !== saved.revision && <p className="notice">The saved cap changed while you were editing. Reload the latest settings before saving.</p>}
      <label className="field">Cap in USD<input className="input" type="number" min="0.01" step="0.01" disabled={busy} value={editor.cap} onChange={e => setEditor({...editor, cap: e.target.value})} /></label>
      <label className="field">Reason for change<input className="input" required maxLength={2000} disabled={busy} value={reason} onChange={e => setReason(e.target.value)} /></label>
      <div className="action-group">
        <button className="btn small" disabled={busy || !reason.trim() || !editor.cap.trim() || editor.revision !== saved?.revision}>Save engagement cap</button>
        <button type="button" className="btn secondary small" disabled={busy || !reason.trim() || saved?.cap_usd === null || editor.revision !== saved?.revision} onClick={() => void save(null)}>Remove engagement cap</button>
        <button type="button" className="btn secondary small" disabled={busy} onClick={() => void run(async () => {
          const value = await api.engagementBudget(engagementId)
          setSaved(value); setLoadError(''); setEditor({revision: value.revision, cap: value.cap_usd === null ? '' : String(value.cap_usd)}); setReason(''); setNotice('Latest settings loaded. Enter a reason before saving.')
        })}>Reload saved settings</button>
        <button type="button" className="btn secondary small" disabled={busy} onClick={() => { setEditor(null); setReason('') }}>Cancel</button>
      </div>
    </form>}
  </section>
}
